import { cn } from "@gettactic/helpers";
import { formatBufferMinutes } from "@gettactic/helpers/src/schedules/scheduleBuffer";
import React from "react";
import { getEventPosition } from "./helpers";

interface CalendarEventBufferProps {
  /** Wall-clock bounds of the band itself. */
  start: Date;
  end: Date;
  minutes: number;
  side: "before" | "after";
  /** The day this column shows, so a band reaching past it is clamped. */
  day: Date;
  /** Same column geometry as the event the band belongs to, in percent. */
  width: number;
  left: number;
}

/**
 * The hatched band a reservation's buffer draws above or below its card in the
 * day view: the resource is held for setup or cleanup, but nothing is booked
 * there, so the band carries no label of its own.
 *
 * A buffer can reach into the previous or next day — a four-hour lead-in on an
 * early-morning meeting, say — and then only the part falling on this column's
 * day is drawn, flush to the edge it was cut at.
 */
export const CalendarEventBuffer: React.FC<CalendarEventBufferProps> = ({
  start,
  end,
  minutes,
  side,
  day,
  width,
  left
}) => {
  if (minutes <= 0) return null;

  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayStartMs = dayStart.getTime();
  // The next local midnight, not midnight plus 24 hours: a DST day is 23 or 25
  // hours long, and the column ends where the wall clock does.
  const dayEnd = new Date(day);
  dayEnd.setHours(24, 0, 0, 0);
  const dayEndMs = dayEnd.getTime();

  const from = Math.max(start.getTime(), dayStartMs);
  const to = Math.min(end.getTime(), dayEndMs);
  if (to <= from) return null;

  const isOuterEdgeOnThisDay =
    side === "before" ? from > dayStartMs : to < dayEndMs;

  return (
    <div
      aria-hidden="true"
      title={`Buffer ${formatBufferMinutes(minutes)} ${side} this reservation`}
      className={cn(
        "buffer-band pointer-events-none absolute",
        isOuterEdgeOnThisDay && (side === "before" ? "rounded-t" : "rounded-b")
      )}
      style={{
        // Placed by wall clock like the event card, so the band stays glued to
        // it across a DST transition; the length is elapsed time, like the
        // card's height.
        top: `${getEventPosition(new Date(from))}px`,
        height: `${(to - from) / 60_000}px`,
        width: `${width}%`,
        left: `${left}%`
      }}
    />
  );
};
